import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, ChevronRight, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable tidy-up dialog that detects + merges (or deletes) duplicate groups.
 *
 * Props:
 *   open / onOpenChange  — control dialog visibility
 *   label                 — singular noun (e.g. "customer", "vendor", "bill")
 *   fetchUrl              — GET endpoint returning { groups: [{id, suggested_canonical_id, members: [...]}]}
 *   mergeUrl              — POST endpoint taking {canonical_id, duplicate_ids}
 *                           When null, each duplicate is DELETE'd individually at `deleteUrlPrefix + id`.
 *   deleteUrlPrefix       — used when mergeUrl is null (e.g. "/bills/")
 *   renderRow(member, isCanonical) → React node for each row body
 *   onDone()              — called after a successful merge so the parent can reload its list
 *   title                 — optional custom dialog title
 */
export default function TidyUpDialog({
    open, onOpenChange,
    label, fetchUrl, mergeUrl, deleteUrlPrefix,
    renderRow, onDone, title,
}) {
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [selections, setSelections] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setGroups([]);
        setSelections({});
        setLoading(true);
        api.get(fetchUrl)
            .then(({ data }) => {
                const gs = data.groups || [];
                setGroups(gs);
                const init = {};
                for (const g of gs) {
                    const dup = new Set(g.members.map((m) => m.id).filter((id) => id !== g.suggested_canonical_id));
                    init[g.id] = { canonicalId: g.suggested_canonical_id, dup };
                }
                setSelections(init);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [open, fetchUrl]);

    const setCanonical = (gid, id) => {
        setSelections((prev) => {
            const g = groups.find((x) => x.id === gid);
            if (!g) return prev;
            const dup = new Set(g.members.map((m) => m.id).filter((mid) => mid !== id));
            return { ...prev, [gid]: { canonicalId: id, dup } };
        });
    };

    const toggleDup = (gid, id) => {
        setSelections((prev) => {
            const cur = prev[gid] || { canonicalId: null, dup: new Set() };
            const next = new Set(cur.dup);
            if (next.has(id)) next.delete(id); else next.add(id);
            return { ...prev, [gid]: { ...cur, dup: next } };
        });
    };

    const total = useMemo(() =>
        Object.values(selections).reduce((a, s) => a + (s.dup?.size || 0), 0),
    [selections]);

    const run = async () => {
        const queue = [];
        for (const g of groups) {
            const s = selections[g.id];
            if (!s || s.dup.size === 0) continue;
            if (mergeUrl) {
                queue.push(api.post(mergeUrl, {
                    canonical_id: s.canonicalId,
                    duplicate_ids: Array.from(s.dup),
                }));
            } else {
                for (const id of s.dup) queue.push(api.delete(`${deleteUrlPrefix}${id}`));
            }
        }
        if (queue.length === 0) {
            toast.error("Nothing selected");
            return;
        }
        setBusy(true);
        try {
            const results = await Promise.allSettled(queue);
            const ok = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.length - ok;
            const removed = results
                .filter((r) => r.status === "fulfilled")
                .reduce((acc, r) => acc + (r.value?.data?.duplicates_removed || 0), 0);
            const pieces = [];
            if (mergeUrl) {
                if (removed) pieces.push(`${removed} duplicate${removed === 1 ? "" : "s"} merged`);
                const docs = results
                    .filter((r) => r.status === "fulfilled")
                    .reduce((a, r) => a + (r.value?.data?.docs_touched || 0), 0);
                if (docs) pieces.push(`${docs} record${docs === 1 ? "" : "s"} updated`);
            } else {
                pieces.push(`${ok} ${label}${ok === 1 ? "" : "s"} deleted`);
            }
            toast.success(pieces.join(" · ") || "Done");
            if (failed) toast.error(`${failed} could not be processed`);
            onOpenChange?.(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const actionVerb = mergeUrl ? "Merge" : "Delete";
    const actionIcon = mergeUrl ? ArrowRightLeft : ChevronRight;
    const Icon = actionIcon;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange?.(v); }}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-secondary" /> {title || `Tidy up ${label}s`}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2" data-testid={`tidy-${label}-body`}>
                    {loading && (
                        <div className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
                            <Loader2 className="h-6 w-6 animate-spin text-secondary" />
                            Scanning for duplicate {label}s…
                        </div>
                    )}
                    {!loading && groups.length === 0 && (
                        <div className="py-16 text-center" data-testid={`tidy-${label}-empty`}>
                            <div className="h-14 w-14 rounded-2xl bg-secondary/10 text-secondary grid place-items-center mx-auto">
                                <Sparkles className="h-7 w-7" />
                            </div>
                            <h3 className="mt-4 font-heading text-xl text-ocean-700">Nothing to tidy</h3>
                            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
                                No duplicate {label}s detected. Your catalog looks clean.
                            </p>
                        </div>
                    )}
                    {!loading && groups.length > 0 && (
                        <div className="space-y-5">
                            <p className="text-sm text-muted-foreground">
                                Found {groups.length} possible duplicate group{groups.length === 1 ? "" : "s"}.
                                Pick the {label} to keep, uncheck anything that isn't actually a duplicate,
                                then {actionVerb.toLowerCase()}. {mergeUrl
                                    ? `Every invoice, bill, and payment referencing a duplicate ${label} will move to the canonical one — no financial data is lost.`
                                    : `The duplicate ${label}s (and any linked payments) will be permanently deleted.`}
                            </p>
                            {groups.map((g) => {
                                const sel = selections[g.id] || { canonicalId: g.suggested_canonical_id, dup: new Set() };
                                return (
                                    <div key={g.id} className="rounded-2xl border border-sand-300 bg-white overflow-hidden" data-testid={`tidy-${label}-group-${g.id}`}>
                                        <div className="px-4 py-3 bg-sand-50 border-b border-sand-200 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                                            <span>Duplicate group</span>
                                            <span className="text-secondary-dark normal-case font-medium">{g.members.length} candidates</span>
                                        </div>
                                        <div className="divide-y divide-sand-100">
                                            {g.members.map((m) => {
                                                const isCanonical = sel.canonicalId === m.id;
                                                const willAct = !isCanonical && sel.dup.has(m.id);
                                                return (
                                                    <div
                                                        key={m.id}
                                                        className={`px-4 py-3 flex items-start gap-3 ${isCanonical ? "bg-secondary/5" : willAct ? "bg-coral/5" : ""}`}
                                                        data-testid={`tidy-${label}-row-${m.id}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={g.id}
                                                            checked={isCanonical}
                                                            onChange={() => setCanonical(g.id, m.id)}
                                                            className="mt-1 h-4 w-4 accent-secondary cursor-pointer"
                                                            aria-label={`Keep ${m.name || m.number || m.id}`}
                                                            data-testid={`tidy-${label}-keep-${m.id}`}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            {renderRow(m, isCanonical)}
                                                            <div className="flex items-center gap-1.5 mt-1">
                                                                {isCanonical && (
                                                                    <Badge className="bg-secondary text-white text-[10px] uppercase tracking-wider">Keep</Badge>
                                                                )}
                                                                {willAct && (
                                                                    <Badge className="bg-coral/15 text-coral-dark text-[10px] uppercase tracking-wider">
                                                                        {mergeUrl ? <><ArrowRightLeft className="h-3 w-3 mr-1" /> Merge into keep</> : "Delete"}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {!isCanonical && (
                                                            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer shrink-0 select-none">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={willAct}
                                                                    onChange={() => toggleDup(g.id, m.id)}
                                                                    className="h-3.5 w-3.5 accent-coral cursor-pointer"
                                                                    data-testid={`tidy-${label}-dup-${m.id}`}
                                                                />
                                                                {actionVerb}
                                                            </label>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <DialogFooter className="flex-shrink-0 flex items-center justify-between gap-3">
                    <div className="text-xs text-muted-foreground hidden sm:block">
                        {total > 0 ? `${total} ${label}${total === 1 ? "" : "s"} selected.` : "Nothing selected."}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid={`tidy-${label}-cancel`}>Cancel</Button>
                        <Button
                            onClick={run}
                            disabled={busy || total === 0 || groups.length === 0}
                            className={mergeUrl ? "bg-ocean-700 text-white" : "bg-coral hover:bg-coral-dark text-white"}
                            data-testid={`tidy-${label}-submit`}
                        >
                            {busy ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Working…</>) : (<><Icon className="h-4 w-4 mr-1.5" /> {actionVerb} {total} {label}{total === 1 ? "" : "s"}</>)}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
